import React, { memo, useMemo } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { Cell } from '@tanstack/react-table';
import { useSelector } from 'react-redux';

import t from 'react-translate';

import { AverageChangeRow } from 'redux/schemas/app/analytics';
import { getAverageChange } from 'redux/selectors/analytics';

import { extraLargeSpacing } from 'styles/global_defaults/scaffolding';

import NvTable, { Column, RowProps } from 'shared/components/nv-table';
import Spinner from 'shared/components/spinner';
import { QuestionCell, ReportColumnHeader } from './utils';


const AverageCell: React.FC<{
  cell: Cell<AverageChangeRow, number>;
  css: SerializedStyles;
}> = memo(({ cell, css: cellStyles }) => {
  if (cell.getValue() === null || cell.getValue() === undefined) {
    return (
      <div css={cellStyles} className='d-flex align-items-center text-regular font-weight-bolder pl-2'>
        <Spinner />
      </div>
    );
  }
  return (
    <div
      css={cellStyles}
      className='d-flex align-items-center text-regular font-weight-bolder pl-2'
    >
      {(cell.getValue() as number).toFixed(1)}
    </div>
  );
});

const ChangeCell: React.FC<{
  cell: Cell<AverageChangeRow, number>;
  css: SerializedStyles
}> = memo(({ cell, css: cellStyles }) => {
  const change = cell.getValue() ?? 0;
  const sign = change > 0 ? '+' : '';

  let changeClass = 'd-flex align-items-center text-regular font-weight-bolder pl-2';
  if (change > 0) {
    changeClass = `${changeClass} text-success`;
  } else if (change < 0) {
    changeClass = `${changeClass} text-danger`;
  } else {
    changeClass = `${changeClass} text-gray-1`;
  }

  if (cell.getValue() === null) {
    return (
      <div className={changeClass}>
        <Spinner />
      </div>
    );
  }

  return (
    <div
      css={cellStyles}
      className={changeClass}
    >
      {sign}{change % 1 === 0 ? change : change.toFixed(2)}%
    </div>
  );
});

const cellComponents: Record<string, React.ComponentType<any>> = {
  question: QuestionCell,
  preAverage: AverageCell,
  postAverage: AverageCell,
  changePercent: ChangeCell,
};

const AverageChangeRowComponent: React.FC<RowProps<AverageChangeRow>> = ({
  row,
  className,
  getCellStyle,
}) => (
  <div className={className}>
    {row.getVisibleCells().map((cell, index) => {
      const CellComponent = cellComponents[cell.column.id];

      return (
        <CellComponent
          css={getCellStyle(index)}
          cell={cell}
          key={cell.id}
        />
      );
    })}
  </div>
);

const AverageChange: React.FC = () => {
  const averageChangeData = useSelector((state) => getAverageChange(state));

  const columns = useMemo<Column<AverageChangeRow>[]>(() => [
    {
      id: 'question',
      header: t.COURSE_ANALYTICS.PRE_POST.REPORT.AVERAGE_CHANGE.QUESTIONS(),
      gridTemplateColumn: '2fr',
      accessorFn: (row) => row.question,
    },
    {
      id: 'preAverage',
      header: t.COURSE_ANALYTICS.PRE_POST.REPORT.AVERAGE_CHANGE.PRE_COURSE(),
      gridTemplateColumn: '1fr',
      accessorFn: (row) => row.preAverage,
    },
    {
      id: 'postAverage',
      header: t.COURSE_ANALYTICS.PRE_POST.REPORT.AVERAGE_CHANGE.POST_COURSE(),
      gridTemplateColumn: '1fr',
      accessorFn: (row) => row.postAverage,
    },
    {
      id: 'changePercent',
      header: t.COURSE_ANALYTICS.PRE_POST.REPORT.AVERAGE_CHANGE.CHANGE(),
      gridTemplateColumn: '1fr',
      accessorFn: (row) => row.changePercent,
    },
  ], []);

  if (averageChangeData.length === 0) {
    return null;
  }

  return (
    <div className='mb-6 mt-4'>
      <h3 className='text-black course-title mb-2'>
        {t.COURSE_ANALYTICS.PRE_POST.REPORT.AVERAGE_CHANGE.TITLE()}
      </h3>
      <NvTable<AverageChangeRow>
        columns={columns}
        data={averageChangeData}
        RowComponent={AverageChangeRowComponent}
        HeaderComponent={(props) => (
          <ReportColumnHeader
            {...props}
            height={extraLargeSpacing}
            getColumnStyle={(headerIndex) => css`
            ${props.getCellStyle(headerIndex)}
            width: ${headerIndex === 0 ? '560px' : 'auto'};
            max-width: ${headerIndex === 0 ? '560px' : '130px'};
          `}
          />
        )}
        className='bg-white'
      />
    </div>
  );
};

export default AverageChange;
